// src/pages/ReportingPage.js
import React, { useEffect, useState, useRef } from 'react';
import api from '../api/axios';

import 'chart.js/auto';
import { Bar, Line } from 'react-chartjs-2';
import { parseISO, format, startOfWeek, startOfMonth } from 'date-fns';
import { formatCurrency } from '../utils/currency';
import useCurrencyCode from '../hooks/useCurrencyCode';

export default function ReportingPage() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groupBy, setGroupBy] = useState('day');
  const [labels, setLabels] = useState([]);
  const [periodTotals, setPeriodTotals] = useState([]);
  const [cumulative, setCumulative] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [todLabels, setTodLabels] = useState(['00-06', '06-12', '12-18', '18-24']);
  const [todTotals, setTodTotals] = useState([0, 0, 0, 0]);

  const barRef = useRef(null);
  const lineRef = useRef(null);
  const todRef = useRef(null);
  const currencyCode = useCurrencyCode();

  const formatKey = (dateObj) => {
    if (groupBy === 'day') return format(dateObj, 'yyyy-MM-dd');
    if (groupBy === 'week')
      return format(startOfWeek(dateObj, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    return format(startOfMonth(dateObj), 'yyyy-MM');
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/receipts', { params: { page: 1, limit: 10000 } });
        const receipts = Array.isArray(res.data)
          ? res.data
          : res.data && res.data.rows
          ? res.data.rows
          : [];

        let filtered = receipts;
        if (startDate) filtered = filtered.filter((r) => new Date(r.createdAt) >= new Date(startDate));
        if (endDate) filtered = filtered.filter((r) => new Date(r.createdAt) <= new Date(endDate));

        const bucket = {};
        filtered.forEach((r) => {
          const d = parseISO(r.createdAt);
          const key = formatKey(d);
          bucket[key] = (bucket[key] || 0) + r.total;
        });

        const sortedKeys = Object.keys(bucket).sort();
        const totals = sortedKeys.map((k) => +bucket[k].toFixed(2));
        const cum = totals.reduce((acc, cur, i) => {
          acc.push((acc[i - 1] || 0) + cur);
          return acc;
        }, []);

        setLabels(sortedKeys);
        setPeriodTotals(totals);
        setCumulative(cum);

        const todBuckets = { '00-06': 0, '06-12': 0, '12-18': 0, '18-24': 0 };
        filtered.forEach((r) => {
          const h = new Date(r.createdAt).getHours();
          const val = parseFloat(r.total) || 0;
          if (h < 6) todBuckets['00-06'] += val;
          else if (h < 12) todBuckets['06-12'] += val;
          else if (h < 18) todBuckets['12-18'] += val;
          else todBuckets['18-24'] += val;
        });
        setTodLabels(Object.keys(todBuckets));
        setTodTotals(Object.values(todBuckets).map((v) => +v.toFixed(2)));
      } catch (err) {
        console.error('Error loading receipts:', err);
      }
    })();
  }, [startDate, endDate, groupBy]);

  useEffect(() => {
    api
      .get('/reports/metrics')
      .then((res) => setMetrics(res.data))
      .catch((err) => console.error('Error loading metrics:', err));
  }, []);

  const exportCSV = () => {
    const header = ['Period', `Sales (${currencyCode})`, `Cumulative (${currencyCode})`];
    const rows = labels.map((lbl, i) => [
      lbl,
      formatCurrency(periodTotals[i], currencyCode),
      formatCurrency(cumulative[i], currencyCode),
    ]);
    const csv = [header.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPNGCharts = () => {
    const charts = [
      { ref: barRef.current, name: `sales_${groupBy}.png` },
      { ref: lineRef.current, name: 'cumulative_sales.png' },
      { ref: todRef.current, name: 'sales_by_time_of_day.png' },
    ];
    charts.forEach(({ ref, name }) => {
      if (ref && typeof ref.toBase64Image === 'function') {
        const url = ref.toBase64Image();
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        link.click();
      }
    });
  };

  const barData = {
    labels,
    datasets: [
      {
        label: `Sales (${groupBy})`,
        data: periodTotals,
        backgroundColor: 'rgba(34,197,94,0.6)',
      },
    ],
  };

  const lineData = {
    labels,
    datasets: [
      {
        label: 'Cumulative Sales',
        data: cumulative,
        borderColor: 'rgba(34,197,94,1)',
        fill: false,
        tension: 0.2,
        pointRadius: 3,
      },
    ],
  };

  const todData = {
    labels: todLabels,
    datasets: [
      {
        label: 'Sales by Time of Day',
        data: todTotals,
        backgroundColor: 'rgba(79, 70, 229, 0.6)',
      },
    ],
  };

  const commonOpts = {
    responsive: true,
    plugins: { legend: { position: 'top' } },
    maintainAspectRatio: false,
  };

  const todOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: 'Sales by Time of Day' },
    },
  };

  const isLoading = labels.length === 0;

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-6 overflow-auto bg-slate-950 px-6 py-6 text-slate-100">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">Sales Reporting</h1>
          <p className="text-sm text-slate-400">
            Analyse performance, export charts, and monitor trends across your till.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={exportCSV}
            className="inline-flex items-center justify-center rounded-full border border-slate-700 bg-slate-900/50 px-4 py-2 text-sm font-semibold uppercase tracking-wide text-slate-200 transition hover:border-slate-500 hover:text-white"
          >
            Download CSV
          </button>
          <button
            onClick={exportPNGCharts}
            className="inline-flex items-center justify-center rounded-full border border-slate-700 bg-slate-900/50 px-4 py-2 text-sm font-semibold uppercase tracking-wide text-slate-200 transition hover:border-slate-500 hover:text-white"
          >
            Download Charts
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center justify-center rounded-full bg-brandGreen px-4 py-2 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-brandDark"
          >
            Export PDF
          </button>
        </div>
      </header>

      <div className="grid gap-6 xl:grid-cols-[1.5fr,1fr]">
        {metrics && (
          <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
            <h2 className="text-lg font-semibold text-slate-100">Key metrics</h2>
            <p className="mt-2 text-sm text-slate-400">
              Average sale:{' '}
              <span className="font-semibold text-slate-100">
                {formatCurrency(metrics.averageSale, currencyCode)}
              </span>
            </p>
            <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Top 5 products
              </h3>
              <ul className="mt-2 space-y-1 text-sm text-slate-300">
                {metrics.topProducts.slice(0, 5).map((prod) => (
                  <li key={prod.productId} className="flex items-center justify-between gap-4">
                    <span className="truncate">{prod.productName}</span>
                    <span className="text-slate-500">{prod.totalSold} sold</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
          <h2 className="text-lg font-semibold text-slate-100">Filters</h2>
          <p className="mt-1 text-sm text-slate-400">Focus the report window and grouping.</p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <label className="space-y-2 text-sm">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Start date
              </span>
              <input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                className="w-full rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-brandGreen/30"
              />
            </label>
            <label className="space-y-2 text-sm">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                End date
              </span>
              <input
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                className="w-full rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-brandGreen/30"
              />
            </label>
            <label className="space-y-2 text-sm">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Group by
              </span>
              <select
                value={groupBy}
                onChange={(event) => setGroupBy(event.target.value)}
                className="w-full rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-brandGreen/30"
              >
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
            </label>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setGroupBy('day');
              }}
              className="inline-flex items-center justify-center rounded-full border border-slate-700 bg-slate-950/50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-300 transition hover:border-slate-500 hover:text-white"
            >
              Reset filters
            </button>
          </div>
        </section>
      </div>

      <section className="grid gap-6 xl:grid-cols-3">
        <ChartCard title={`Sales (${groupBy})`} isLoading={isLoading}>
          <Bar ref={barRef} data={barData} options={commonOpts} />
        </ChartCard>
        <ChartCard title="Cumulative sales" isLoading={isLoading}>
          <Line ref={lineRef} data={lineData} options={commonOpts} />
        </ChartCard>
        <ChartCard title="Sales by time of day" isLoading={isLoading}>
          <Bar ref={todRef} data={todData} options={todOptions} />
        </ChartCard>
      </section>

      <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
          Totals table
        </h3>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-800 text-sm">
            <thead className="bg-slate-950/60">
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="px-3 py-2 font-semibold">Period</th>
                <th className="px-3 py-2 font-semibold">Sales ({currencyCode})</th>
                <th className="px-3 py-2 font-semibold">Cumulative ({currencyCode})</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {isLoading ? (
                <tr>
                  <td className="px-3 py-3 text-slate-400" colSpan={3}>
                    No data available for the selected filters.
                  </td>
                </tr>
              ) : (
                labels.map((label, index) => (
                  <tr key={label} className="text-slate-200">
                    <td className="px-3 py-2">{label}</td>
                    <td className="px-3 py-2">
                      {formatCurrency(periodTotals[index], currencyCode)}
                    </td>
                    <td className="px-3 py-2">
                      {formatCurrency(cumulative[index], currencyCode)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function ChartCard({ title, isLoading, children }) {
  return (
    <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">{title}</h3>
      <div className="mt-4 h-64">
        {isLoading ? (
          <EmptyState message="Loading data..." />
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-slate-800 bg-slate-950/40 text-xs text-slate-500">
      {message}
    </div>
  );
}
